import {ControlledFormField, FormFieldBase} from '@hconnect/common/types'
import {TaskChecklistsField} from '@widgets/hroc-checklists'
import {useEffect} from 'react'
import {Controller, FieldValues, useFormContext} from 'react-hook-form'

import {useTaskChecklists} from '../../hooks'

type Props<TField extends FieldValues> = FormFieldBase &
  ControlledFormField<TField> & {
    plantId: string
    externalTaskId?: string
    isEditMode?: boolean
  }

export const TaskChecklistsFormField = <TField extends FieldValues>(props: Props<TField>) => {
  const {plantId, externalTaskId, isEditMode} = props
  const {setValue, getValues} = useFormContext()

  const shouldFetchChecklists = isEditMode && !!externalTaskId && !!plantId

  const {data: taskChecklists = []} = useTaskChecklists(
    {
      plantId,
      externalId: externalTaskId ?? ''
    },
    {
      enabled: shouldFetchChecklists
    }
  )

  // Initialize checklists in edit mode
  useEffect(() => {
    if (!shouldFetchChecklists || !taskChecklists.length) return

    // Get current form value to avoid overwriting existing selections
    const currentValue = getValues(props.name)

    // Only set initial value if no checklists are currently selected
    if (!currentValue || currentValue.length === 0) {
      setValue(props.name as any, [{id: taskChecklists[0].id, kind: taskChecklists[0].kind}])
    }
  }, [taskChecklists, shouldFetchChecklists, setValue, getValues, props.name])

  return (
    <Controller
      name={props.name}
      control={props.control}
      disabled={props.disabled || props.readOnly}
      render={({field, fieldState}) => (
        <TaskChecklistsField
          plantId={props.plantId}
          errorText={fieldState.error?.message}
          onChange={(value) => {
            field.onChange(value)
          }}
          value={field.value}
          disabled={props.disabled}
          isOptionEqualToValue={(option, val) => option.id === val.id}
        />
      )}
    />
  )
}
